import { config } from "@/config";
import { isUuid } from "@/lib/uuid";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { apiFetch } from "@/lib/api-client";
import { useMutation, useQuery } from "@tanstack/react-query";

export interface JiraBoardOption {
  id: number;
  projectKey: string;
  domain: string;
  label: string;
}

export interface JiraAssignableUser {
  accountId: string;
  displayName: string;
  emailAddress?: string;
  avatarUrl?: string;
  active: boolean;
}

export interface JiraTicketDraft {
  title: string;
  description: string;
}

export interface CreatedJiraIssue {
  key: string;
  id: string;
  url: string;
}

export interface ChatTranscriptTurn {
  role: "user" | "assistant";
  text: string;
}

const ENDPOINT = `${config.isometricApiUrl}/jira-issues`;

// Note the absence of a `selectFields` parameter here. The board list is a fixed
// server-side projection of id / projectKey / domain / label and deliberately has
// no way to request the Atlassian `token` — unlike use-jira-config-service, which
// does ask for it. Do not add one: the browser never needs the credential, it
// references a board by id and the backend resolves the token per request.

async function readError(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => ({}));
  return body?.message || fallback;
}

async function listJiraBoards(
  projectUuid: string,
  token: string,
): Promise<JiraBoardOption[]> {
  const response = await apiFetch(
    `${ENDPOINT}/boards?projectUuid=${encodeURIComponent(projectUuid)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(await readError(response, "Failed to load Jira boards."));
  }

  const body = await response.json();
  return (body?.data ?? []) as JiraBoardOption[];
}

async function listJiraAssignableUsers(
  projectUuid: string,
  jiraConfigId: number,
  token: string,
): Promise<JiraAssignableUser[]> {
  const response = await apiFetch(
    `${ENDPOINT}/assignable-users?projectUuid=${encodeURIComponent(projectUuid)}` +
      `&jiraConfigId=${encodeURIComponent(String(jiraConfigId))}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(await readError(response, "Failed to load assignees."));
  }

  const body = await response.json();
  return (body?.data ?? []) as JiraAssignableUser[];
}

async function draftJiraTicket(
  projectUuid: string,
  messages: ChatTranscriptTurn[],
  issueType: string | undefined,
  token: string,
): Promise<JiraTicketDraft> {
  const response = await apiFetch(`${ENDPOINT}/draft`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ projectUuid, messages, issueType }),
  });

  if (!response.ok) {
    throw new Error(
      await readError(response, "Couldn't draft the ticket automatically."),
    );
  }

  return (await response.json()) as JiraTicketDraft;
}

async function createJiraIssue(
  payload: {
    projectUuid: string;
    jiraConfigId: number;
    title: string;
    description?: string;
    issueType?: string;
    assignee?: string;
    priority?: string;
    labels?: string[];
    environment?: string;
  },
  token: string,
): Promise<CreatedJiraIssue> {
  const response = await apiFetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    // Jira's own field-level message arrives here on a 422 (a required custom
    // field, an issue type the project doesn't define). Surface it unchanged —
    // it is the only text that tells the user what to fix.
    throw new Error(await readError(response, "Failed to create the Jira ticket."));
  }

  return (await response.json()) as CreatedJiraIssue;
}

/**
 * Linked Jira boards for the board dropdown.
 *
 * Doubles as the config probe behind the button's visibility: an empty array
 * means nothing is linked, so the "Raise Jira Ticket" button stays hidden. That
 * avoids a second request purely to ask "is Jira set up?".
 */
export function useListJiraBoards(projectUuid: string | undefined) {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["jira-issues", "boards", projectUuid],
    queryFn: () => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return listJiraBoards(projectUuid as string, token);
    },
    enabled: isUuid(projectUuid),
    // No retries: this doubles as a capability probe, and its failure modes are
    // all terminal for the current user — a 403 for a Viewer, or a 409 when no
    // board is linked. Retrying just triples the noise on every chat load.
    retry: false,
  });
}

export function useDraftJiraTicket() {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: ({
      projectUuid,
      messages,
      issueType,
    }: {
      projectUuid: string;
      messages: ChatTranscriptTurn[];
      issueType?: string;
    }) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return draftJiraTicket(projectUuid, messages, issueType, token);
    },
  });
}

/**
 * Assignable users for a chosen board. Enabled only when a board is selected —
 * the endpoint is scoped per jiraConfigId, so there is nothing to fetch until
 * the user picks one. Re-fetches on board change.
 */
export function useListJiraAssignableUsers(
  projectUuid: string | undefined,
  jiraConfigId: number | undefined,
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["jira-issues", "assignable-users", projectUuid, jiraConfigId],
    queryFn: () => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return listJiraAssignableUsers(
        projectUuid as string,
        jiraConfigId as number,
        token,
      );
    },
    enabled: !!projectUuid && !!jiraConfigId,
    // Same rationale as boards: failure modes are terminal for the current user
    // (bad credentials on the board, project missing on the Atlassian side).
    retry: false,
  });
}

export function useCreateJiraIssue(
  onSuccess?: (data: CreatedJiraIssue) => void,
  onError?: (error: Error) => void,
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (payload: {
      projectUuid: string;
      jiraConfigId: number;
      title: string;
      description?: string;
      issueType?: string;
      assignee?: string;
      priority?: string;
      labels?: string[];
      environment?: string;
    }) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return createJiraIssue(payload, token);
    },
    onSuccess,
    onError,
  });
}
